import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { giraffeTrip } from '@/lib/giraffePaths';
import ZEmptyState from '../shared/ZEmptyState';
import ZLoadingBar from '../shared/ZLoadingBar';

/**
 * GiraffeMemoirsTab — read-only cross-trip index of published memoirs.
 *
 * Fetches /api/giraffe/memoirs which returns one row per published memoir
 * with trip name, memoir title, published date, and fragment count.
 * Tapping a row navigates to the trip page. The memoir lives on the trip,
 * this tab indexes it — it does not own the data.
 */
export default function GiraffeMemoirsTab() {
  const [memoirs, setMemoirs] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/giraffe/memoirs')
      .then(({ data }) => setMemoirs(Array.isArray(data) ? data : []))
      .catch(() => setMemoirs([]));
  }, []);

  if (memoirs === null) {
    return (
      <div style={{ padding: '48px 24px' }}>
        <ZLoadingBar accent="#10B981" loading={true} />
      </div>
    );
  }

  if (memoirs.length === 0) {
    return (
      <ZEmptyState
        mark="G"
        accent="#10B981"
        message="No published memoirs yet"
        hint="Memoirs are published from the trip page."
      />
    );
  }

  return (
    <div>
      {memoirs.map((m) => (
        <div
          key={m.trip_id}
          onClick={() => navigate(giraffeTrip(m.trip_id))}
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 16,
            padding: '14px 16px',
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderLeft: '3px solid #10B981',
            borderRadius: 10,
            marginBottom: 8,
            cursor: 'pointer',
            transition: 'all 150ms ease',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)';
            e.currentTarget.style.borderLeftColor = '#10B981';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
            e.currentTarget.style.borderLeftColor = '#10B981';
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 16,
              color: '#f0f0f5',
              marginBottom: 4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {m.memoir_title}
            </div>
            <div style={{
              fontSize: 11,
              color: '#b0b4c0',
              fontFamily: "'Inter', sans-serif",
              fontWeight: 300,
            }}>
              {m.trip_name}
            </div>
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 4,
            flexShrink: 0,
          }}>
            <span style={{
              fontSize: 10,
              fontFamily: 'monospace',
              color: '#6b7084',
              letterSpacing: '0.05em',
            }}>
              {formatDate(m.published_at)}
            </span>
            <span style={{
              fontSize: 10,
              fontFamily: 'monospace',
              color: '#6b7084',
              letterSpacing: '0.05em',
            }}>
              {m.fragment_count} fragment{m.fragment_count === 1 ? '' : 's'}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  } catch {
    return '';
  }
}
